import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// Components
import TagInput from '../../Elements/TagInput';

// Deps
import { DAY_FORMAT, ISSUE_TYPES, ORDER_STATUSES_DISPLAY, ORDER_TYPES_DISPLAY } from '../../../constants';
import { getPartnerItemTypes } from '../../../modules/partners/api';
import './OrderInfoForm.scss';

const OrderInfoForm = ({ order, types, setTypes, partners = [] }) => {
  const form = Form.useFormInstance();
  const selectedPartnerId = Form.useWatch('partnerId', form);
  const productDetailsKnown = Form.useWatch('productDetailsKnown', form);

  const [productTypes, setProductTypes] = useState([]);

  // Fetch PRODUCT item types when partner changes
  useEffect(() => {
    if (!selectedPartnerId) {
      setProductTypes([]);
      return;
    }
    getPartnerItemTypes(selectedPartnerId).then(({ response }) => {
      const items = Array.isArray(response) ? response : [];
      setProductTypes(items.filter((t) => t.category === 'PRODUCT' && t.isActive));
    });
  }, [selectedPartnerId]);

  return (
    <div className={`OrderInfoForm`}>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="Name" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Order Ref" name="orderRef">
            <Input placeholder="Order Ref" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="deliveryDate" label="Delivery Date" rules={[{ required: true, message: 'Required' }]}>
            <DatePicker format={DAY_FORMAT} disabled />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="orderType" label="Order Type">
            <Select
              disabled
              options={Object.keys(ORDER_TYPES_DISPLAY).map((key) => ({ value: key, label: ORDER_TYPES_DISPLAY[key] }))}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="volume" label="Volume">
            <Input min={0} step={0.001} type="number" placeholder="Volume" suffix="m³" />
          </Form.Item>
        </Col>
        {order && (
          <Col span={6}>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Required' }]}>
              <Select
                options={Object.keys(ORDER_STATUSES_DISPLAY).map((key) => ({
                  value: key,
                  label: ORDER_STATUSES_DISPLAY[key],
                }))}
                placeholder="Order Status"
              />
            </Form.Item>
          </Col>
        )}
        <Col span={4}>
          <Form.Item name="sizeLength" label="Size (LxWxH)">
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="sizeWidth" label=" ">
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="sizeHeight" label=" ">
            <Input min={1} type="number" suffix="cm" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="weight" label="Weight">
            <Input min={1} type="number" suffix="kg" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Support Note" name="supportNote">
            <Input.TextArea placeholder="Support Note" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Description" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Issue Type" name="issueType">
            <Select
              placeholder="Select Issue Type"
              options={Object.keys(ISSUE_TYPES).map((key) => ({ value: key, label: ISSUE_TYPES[key] }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Partner" name="partnerId">
            <Select
              placeholder="Select a partner"
              showSearch
              allowClear
              optionFilterProp="label"
              options={partners.map((p) => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Type" name="types">
            <TagInput size="normal" tags={types} onChange={(values) => setTypes(values)} />
          </Form.Item>
        </Col>
      </Row>

      {/* Order Products Section — only when partner has PRODUCT item types */}
      {selectedPartnerId && productTypes.length > 0 && (
        <>
          <Divider orientation="left">Products</Divider>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="productDetailsKnown" valuePropName="checked" style={{ marginBottom: 12 }}>
                <Checkbox>Product details known at order creation</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {productDetailsKnown && (
            <Form.List name="orderProducts">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                      <Col span={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'partnerItemTypeId']}
                          rules={[{ required: true, message: 'Select product' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder="Select product"
                            options={productTypes.map((t) => ({
                              label: `${t.name}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
                              value: t.id,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          rules={[{ required: true, message: 'Qty' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber min={0.01} step={0.1} placeholder="Qty" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...restField} name={[name, 'notes']} style={{ marginBottom: 0 }}>
                          <Input placeholder="Notes (optional)" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ quantity: 1 })}
                    icon={<PlusOutlined />}
                    style={{ width: '50%' }}
                  >
                    Add Product
                  </Button>
                </>
              )}
            </Form.List>
          )}
          <Divider />
        </>
      )}

      <Row gutter={24}>
        <Col span={6}>
          <Form.Item label="Partner Contact" name="partnerContact">
            <Input placeholder="Partner Contact" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Partner Order Ref" name="partnerOrderRef">
            <Input placeholder="Partner Order Ref" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Tour Ref" name="tourRef">
            <Input placeholder="Tour Ref" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Airtable Order Record Ref" name="airtableOrderRecordRef">
            <Input placeholder="Airtable Order Record Ref" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Preparation Mode" name="preparationMode">
            <Input placeholder="Preparation Mode" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Collection Form" name="collectionForm">
            <Input placeholder="Collection Form" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default OrderInfoForm;
